import type { RootState } from '../../../../store/models';
import type { ProviderService } from '../../../../api/Providers/Services';
import { UserAppointment } from '../../../../api/Users/Appointments';

const MAX_LAST_SERVICES = 4;

export const buildServices = (servicesById: {
  [key: number]: ProviderService
} | undefined) => servicesById && Object.values(servicesById || {})
  .map(service => ({
    ...service,
    isTopService: true,
  }));

const sortAndFilterServices = (
  appointments: Pick<UserAppointment, 'time_blocks'>[] | null,
  services: ProviderService[] | null,
) => {
  const previouslyBookedServices: {
    [key: number]: number,
  } = {};

  if (appointments == null || services == null) {
    return null;
  }

  appointments.concat().reverse().forEach((appointment, index) => {
    appointment.time_blocks.forEach(timeBlock => {
      previouslyBookedServices[timeBlock.service] = Math.max(
        index,
        previouslyBookedServices[timeBlock.service] || 0,
      );
    });
  });

  return services
    .filter(service => service.id in previouslyBookedServices)
    .sort((a, b) => previouslyBookedServices[b.id] - previouslyBookedServices[a.id]);
};

export const trimServices = (
  services: ProviderService[] | null,
  maxLength: number,
): Array<ProviderService> | null => {
  if (services == null) {
    return null;
  }
  return services.slice(0, Math.min(maxLength, services.length));
};

/**
 * Builds a list of previously booked services
 * @param state The current redux state
 * @param provider The provider to key off of
 * @returns A list of previously booked services, sorted in order of ID, with a maximum length of 4
 */
export function buildPreviouslyBookedServices(
  state: RootState,
  provider: { id: number },
): ProviderService[] | null {
  const services = buildServices(state.profileServiceGroups?.[provider.id]?.servicesById);
  const myAppointmentsData = state.previousAppointments.byProvider?.[provider.id];
  const myAppointments = (
    myAppointmentsData && 'previousAppointments' in myAppointmentsData
      ? myAppointmentsData.previousAppointments.results
      : null
  );

  return trimServices(
    sortAndFilterServices(myAppointments, services),
    MAX_LAST_SERVICES,
  );
}

/**
 * Builds a list of services ordered by popularity
 * @param state The current redux state
 * @param provider The provider to key off of
 * @returns The list of services, sorted by popularity, including only those with a valid popularity
 */
export function buildPopularServices(
  state: RootState,
  provider: { id: number },
): ProviderService[] | null {
  return (buildServices(state.profileServiceGroups?.[provider.id]?.servicesById) || [])
    .filter(service => typeof service.popularity_percentage === 'number')
    .sort((a, b) => b.popularity_percentage - a.popularity_percentage);
}
